import React from 'react';
import { Text } from '@fluentui/react-components';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { ChatStudentBadge } from '../ChatBadge/ChatStudentBadge';
import { ChatAvatar } from '../ChatAvatar/ChatAvatar';
import { ChatIconsLocked } from '../ChatIcons/ChatIconsLocked';
import './ChatListEntry.scss';
import {
  ChatMessagePreview,
  ChatThreadType,
  GuardianChatPerson,
} from '@mnspro-cloud/services/lib/mnspro-cloud/api/v2';
import { formatChatMessageDate, isZeroGuid } from '../Chat.utils';
import { useChatDraft } from '../drafts';
import { ThemeMode, useProviderTheme } from '../../hooks';

interface ChatListEntryProps {
  name: string;
  ward?: GuardianChatPerson;
  type: ChatThreadType;
  isLocked?: boolean;
  lastMessage?: ChatMessagePreview;
  onClick: () => void;
  selected?: boolean;
  notificationCount?: number;
  /** Thread id used to show an unsent draft as the last-message preview. */
  threadId?: string;
}

export const ChatListEntry: React.FC<ChatListEntryProps> = ({
  name,
  ward,
  type,
  isLocked,
  lastMessage,
  onClick,
  selected,
  notificationCount,
  threadId,
}) => {
  const { t } = useTranslation();
  const [draft] = useChatDraft(threadId);
  const theme = useProviderTheme();

  const draftText = draft.text.trim() !== '' ? draft.text.trim() : draft.attachment?.fileName;
  const draftPrefix = t('pc:chat:draft_prefix', { defaultValue: '[Entwurf]' });

  const lastMsgText = draftText
    ? `${draftPrefix} ${draftText}`
    : lastMessage
    ? `${lastMessage.senderDisplayName}: ${lastMessage.content}`
    : t('pc:chat:no_message_yet', { defaultValue: 'Noch keine Nachricht vorhanden' });

  const ariaLabel = t('pc:chat:open_chat_aria', {
    defaultValue: 'Öffnet den Chat "{{name}}".',
    name,
  });

  const dateText = formatChatMessageDate(lastMessage?.sentOn);

  return (
    <div
      className={cn('ChatListEntry', {
        selected,
        highContrast: theme.mode === ThemeMode.HighContrast,
      })}
      onClick={onClick}
      aria-label={ariaLabel}
      aria-current={selected ? 'true' : 'false'}
    >
      <ChatAvatar
        variant={type === ChatThreadType.direct ? 'direct' : 'group'}
        ward={ward}
        badgeCount={notificationCount && notificationCount > 0 ? notificationCount : undefined}
      />
      <div className="inner">
        <div className="titleWrapper">
          {type === ChatThreadType.direct && ward && !isZeroGuid(ward.id) && (
            <ChatStudentBadge userId={ward.id} displayName={ward.displayName} />
          )}
          {isLocked && (
            <div className="icon" aria-hidden="true">
              <ChatIconsLocked />
            </div>
          )}
          <div className="title">{name}</div>
        </div>

        {/* Sibling of the title row on purpose: the grid on `.inner` places it next to
            the title, or in front of the last message once the entry gets narrow. */}
        {dateText !== '' && <div className="date">{dateText}</div>}

        <Text className={cn('lastMsg', { draft: !!draftText })}>{lastMsgText}</Text>
      </div>
    </div>
  );
};
